import {
	SuperGridPluginParams, useSuperGridValidate, SuperGridValidationEntity,
} from '@aleph/supergrid';
import { useApolloClient } from '@apollo/client';
import {
	GraphQLErrorWithFormValidation,
	mapDevicesToTargeting,
	mapValidationErrors,
	RedditLevel,
} from 'wb-reddit/app/routes/superGrid/utils';
import { LEVEL_ID_FILTER } from 'wb-reddit/app/routes/superGrid/actionRow/filters/utils';
import {
	CreativeType,
	GroupTargetingDevicesOs,
	GroupTargetingLocations,
	UpdateAdInput,
	UpdateCampaignInput,
	UpdateCreativeInput,
	UpdateEntitiesDocument,
	UpdateEntitiesMutationAdUpdates,
	UpdateGroupInput,
} from 'wb-reddit/graphql/generated';
import {
	debounce, merge, omit, cloneDeep,
} from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import {
	alephToast, DATE_TIME_ISO_FORMAT,
	NotificationToast,
	NotificationType,
	UndoIcon,
} from '@aleph/shared-components';
import { transformRedditRows } from 'wb-reddit/app/routes/superGrid/config';
import { captureException } from '@sentry/react';
import { validateRedditEntity } from 'wb-reddit/app/routes/superGrid/validation/utils';
import { CellValueChangedEvent } from 'ag-grid-community';
import dayjs from 'dayjs';

const getCreativeDataDefault = (data: any) => {
	const newData = cloneDeep(data.flatCreativeContent);

	if (!Array.isArray(newData) || newData.length === 0) {
		return [];
	}

	newData.forEach((item) => {
		delete item.creativeContentId;
		delete item.__typename;
	});

	return newData;
};

function parseAndSetNumericValue(fieldName: string) {
	return (val: string | number) => ({ [fieldName]: Number(val) });
}

const UPDATE_FIELD_MAPS = {
	status: (status: string, level: RedditLevel, data: Record<string, any>) => ({ [`${level}Status`]: data?.statusPending }),
	name:   (name: string, level: RedditLevel) => ({ [`${level}Name`]: name }),
	gender: (gender: string) => ({
		groupTargeting: {
			groupTargetingGender: gender,
		},
	}),
	placements: (placements?: { value: GroupTargetingLocations }[]) => ({
		groupTargeting: {
			groupTargetingLocations: placements?.map(({ value }) => value) ?? [],
		},
	}),
	locations: (locations?: { value: string }[]) => ({
		groupTargeting: {
			groupTargetingGeolocations: locations?.map(({ value }) => value) ?? [],
		},
	}),
	locationsExcluded: (locations?: { value: string }[]) => ({
		groupTargeting: {
			groupTargetingExcludedGeolocations: locations?.map(({ value }) => value) ?? [],
		},
	}),
	communities: (communities?: { value: string }[]) => ({
		groupTargeting: {
			groupTargetingCommunities: communities?.map(({ value }) => value) ?? [],
		},
	}),
	communitiesExcluded: (communities?: { value: string }[]) => ({
		groupTargeting: {
			groupTargetingExcludedCommunities: communities?.map(({ value }) => value) ?? [],
		},
	}),
	interests: (interests?: { value: string }[]) => ({
		groupTargeting: {
			groupTargetingInterests: interests?.map(({ value }) => value) ?? [],
		},
	}),
	keywords: (keywords?: string[]) => ({
		groupTargeting: {
			groupTargetingKeywords: keywords ?? [],
		},
	}),
	carriers: (carriers?: { value: string }[]) => ({
		groupTargeting: {
			groupTargetingCarriers: carriers?.map(({ value }) => value) ?? [],
		},
	}),
	customAudiences: (customAudiences?: { value: string }[]) => ({
		groupTargeting: {
			groupTargetingCustomAudienceIds: customAudiences?.map(({ value }) => value) ?? [],
		},
	}),
	customAudiencesExcluded: (customAudiences?: { value: string }[]) => ({
		groupTargeting: {
			groupTargetingExcludedCustomAudienceIds: customAudiences?.map(({ value }) => value) ?? [],
		},
	}),
	deviceType: (deviceType: { value: GroupTargetingDevicesOs | 'DESKTOP' }[], _, data: Record<string, any>) => ({
		groupTargeting: {
			groupTargetingDevices: {
				...mapDevicesToTargeting({
					...data,
					deviceType,
				}),
			},
		},
	}),
	groupTargetingDevicesOSMinVersion: (groupTargetingDevicesOSMinVersion: string, _, data: Record<string, any>) => ({
		groupTargeting: {
			groupTargetingDevices: {
				...mapDevicesToTargeting({
					...data,
					groupTargetingDevicesOSMinVersion,
				}),
			},
		},
	}),
	devices: (devices: { value: string, label: string, make: string }[], _, data: Record<string, any>) => ({
		groupTargeting: {
			groupTargetingDevices: {
				...mapDevicesToTargeting({
					...data,
					devices,
				}),
			},
		},
	}),
	adViewEventTrackers: (value, _, data: Record<string, any>) => ({
		adEventTrackers: [
			...value ?? [],
			...data?.adClickEventTrackers ?? [],
		].map(tracker => omit(tracker, '__typename')),
	}),
	adClickEventTrackers: (value, _, data: Record<string, any>) => ({
		adEventTrackers: [
			...value ?? [],
			...data?.adViewEventTrackers ?? [],
		].map(tracker => omit(tracker, '__typename')),
	}),
	groupStartTime: (value: string) => ({
		groupStartTime:	dayjs(value).format(DATE_TIME_ISO_FORMAT),
	}),
	groupEndTime: (value?: string) => ({
		groupEndTime: (value	? dayjs(value).format(DATE_TIME_ISO_FORMAT) : null),
	}),
	...([
		'creativeContentDestinationUrl',
		'creativeContentCallToAction',
		'creativeContentDestinationUrl',
		'creativeContentDisplayUrl',
		'creativeContentCaption',
		'creativeContentMediaUrl',
	].reduce((keyBuilder, fieldName) => ({
		...keyBuilder,
		...([...Array(6).fill(null)].reduce((accu, _, index) => {
			accu[`${fieldName}${index}`] = (
				value: string | { mediaUrl: string } | undefined,
				__: string,
				data: any,
				accumulator: any,
			) => {
				const creativeContent = accumulator.creativeContent ?? getCreativeDataDefault(data);

				if (!creativeContent[index]) {
					creativeContent[index] = {};
				}
				creativeContent[index][fieldName] = typeof value === 'string' ? value : value?.mediaUrl;

				return {
					creativeContent,
				};
			};

			return accu;
		}, {})),
	}), {})
	),
	groupBidAmount:   parseAndSetNumericValue('groupBidAmount'),
	campaignSpendCap: parseAndSetNumericValue('campaignSpendCap'),
	groupGoalAmount:  parseAndSetNumericValue('groupGoalAmount'),
	fundingInstrument(val: string) {
		return {
			fundingInstrumentId: val,
		};
	},
};

const getUpdateMutationVariables = (changes: Record<string, any>, level: RedditLevel, data: Record<string, any>) => Object.keys(changes).reduce((accu, field) => {
	if (UPDATE_FIELD_MAPS[field]) {
		return {
			...accu,
			...UPDATE_FIELD_MAPS[field](changes[field], level, data, accu),
		};
	}

	accu[field] = changes[field];

	return accu;
}, {});

const getSuccessfulEvents = (events: CellValueChangedEvent[], data: Record<string, any>[]) => data.reduce(
	(accu, entity) => {
		const affectedEvents = events.filter(e => e.data?.id === entity?.id);

		if (affectedEvents?.length > 0) {
			affectedEvents.forEach(event => {
				accu.push({
					...event,
					data: entity,
				});
			});
		}

		return accu;
	},
	[],
);

const getAndMapGeneralErrors = (errors: readonly GraphQLErrorWithFormValidation[], level?: RedditLevel) => errors?.filter(error => !error?.form)?.map(
	error => ({
		level,
		id:      `general_${error?.hash ?? performance.now()}`,
		field:   'generalError',
		message: error?.debugMessage ?? error?.message ?? 'Unknown Error',
	}),
) ?? [];

const mergeVariablesByIds = <T extends UpdateCampaignInput | UpdateGroupInput | UpdateAdInput | UpdateCreativeInput>(variables: Array<T & { id: string }>): Array<T> => {
	const variablesById = {};

	variables.forEach(variable => {
		variablesById[variable.id] = merge(
			variablesById[variable.id] ?? {},
			{
				...variable,
				id: undefined,
			},
		);
	});

	return Object.values(variablesById);
};

const useRedditEditPlugin = ({ gridApi }: SuperGridPluginParams) => {
	const client = useApolloClient();
	const { context: { setValidations } } = useSuperGridValidate({
		validate: validateRedditEntity,
		gridApi,
	});
	const [stagedChangeEvents, setStagedChangeEvents] = useState<CellValueChangedEvent[]>([]);
	const [undoEvents, setUndoEvents] = useState<CellValueChangedEvent[]>([]);
	const [savingEvents, setSavingEvents] = useState<CellValueChangedEvent[]>([]);

	useEffect(() => {
		(gridApi?.getColumns() ?? []).forEach(col => {
			col.setColDef(
				col.getColDef(),
				{
					cellRendererParams: params => ({
						isSaving: (savingEvents.some(e => e.node === params.node && e.column === params.column)),
						doUndo:   (undoEvents.some(e => e.node === params.node && e.column === params.column))
							? () => {
								params.node.setDataValue(
									params.column,
									undoEvents.find(e => e.node === params.node && e.column === params.column)?.oldValue,
									'undoChange',
								);
								setUndoEvents(curr => curr.filter(
									e => e.node !== params.node || e.column !== params.column,
								));
							} : undefined,
					}),
				},
				'api',
			);
		});
	}, [undoEvents, savingEvents]);

	const doUpdates = async (events: CellValueChangedEvent[]) => {
		const validEvents: CellValueChangedEvent[] = [];
		const invalidEvents: CellValueChangedEvent[] = [];
		setSavingEvents(events);

		// eslint-disable-next-line no-restricted-syntax
		for (const e of events) {
			// eslint-disable-next-line no-await-in-loop
			const preValidations = await validateRedditEntity({
				changeEvent:      e,
				level:            e?.data?.level as RedditLevel,
				validationErrors: [],
			});

			if (preValidations.length > 0) {
				setValidations(
					curr => [
						...curr.filter(
							validation => validation.id !== e.data?.id && validation.level === e.data?.level,
						),
						...preValidations.filter(validation => !!validation.id) as SuperGridValidationEntity[],
					],
				);

				invalidEvents.push(e);
			} else {
				setValidations(curr => curr.filter(
					validation => validation.id !== e.data?.id && validation.level === e.data?.level,
				));
				validEvents.push(e);
			}
		}

		setStagedChangeEvents(invalidEvents);
		setSavingEvents(validEvents);

		if (validEvents.length === 0) {
			return;
		}

		const variables: {
			campaignChanges: Array<UpdateCampaignInput & { id: string }>,
			groupChanges: Array<UpdateGroupInput & { id: string }>,
			adChanges: Array<UpdateAdInput & { id: string }>,
			creativeChanges: Array<UpdateCreativeInput & { id: string }>,
		} = validEvents.reduce((accu, e) => {
			const changeName = (e.colDef.field ?? e.colDef.colId)?.includes('creative') ? 'creative' : e.data.level;
			const newMutation = {
				id:                            e.data.id,
				[`${changeName}Hash`]:         e.data.id,
				[LEVEL_ID_FILTER[changeName]]: changeName === 'creative' ? e.data.creativeId : e.data.id,
				[e.colDef.field ?? '']:        e.newValue,
			};

			// Video creatives must have a creativeThumbnail url so we have to send it even if it hasn't changed
			if (e.data.creativeType === CreativeType.Video && !newMutation.creativeThumbnailUrl) {
				newMutation.creativeThumbnailUrl = e.data.creativeThumbnailUrl;
			}
			accu[`${changeName}Changes`] = [
				...(accu[`${changeName}Changes`] ?? []),
				getUpdateMutationVariables(newMutation, e.data.level, e.data),
			];

			return accu;
		}, {
			campaignChanges: [],
			groupChanges:    [],
			adChanges:       [],
			creativeChanges: [],
		});

		const { data, errors } = await client.mutate({
			mutation:  UpdateEntitiesDocument,
			variables: {
				campaignChanges: mergeVariablesByIds<UpdateCampaignInput>(variables.campaignChanges),
				groupChanges:    mergeVariablesByIds<UpdateGroupInput>(variables.groupChanges),
				adChanges:       mergeVariablesByIds<UpdateAdInput>(variables.adChanges),
				creativeChanges: mergeVariablesByIds<UpdateCreativeInput>(variables.creativeChanges),
			},
			errorPolicy: 'all',
		});

		const castErrors = (errors ?? []) as unknown as GraphQLErrorWithFormValidation[];

		const validationErrors = [
			...mapValidationErrors(
				castErrors,
			),
			...getAndMapGeneralErrors(errors ?? []),
		];

		if (validationErrors.length > 0) {
			setTimeout(() => {
				setValidations(validationErrors);
			});
		}

		if (data?.creativeUpdates) {
			data.adUpdates = data?.creativeUpdates.reduce((accu, creativeUpdate) => {
				const matchingEvent = validEvents.find(
					(validEvent) => validEvent?.data?.id === creativeUpdate?.creativeHash ?? '',
				);

				if (matchingEvent) {
					accu.push({ ...matchingEvent.data, flatCreative: creativeUpdate });
				}

				return accu;
			}, [] as Array<UpdateEntitiesMutationAdUpdates>);
		}

		const successfulEvents = getSuccessfulEvents(validEvents, [
			...(data?.campaignUpdates ?? []),
			...(data?.groupUpdates ?? []),
			...(data?.adUpdates ?? []),
		]);

		const successfulUndoEvents = successfulEvents?.filter(e => e.source === 'undoChange');
		const successfulUpdateEvents = successfulEvents?.filter(e => e.source !== 'undoChange');

		if (successfulUndoEvents.length > 1) {
			alephToast(
				({ closeToast }) => (
					<NotificationToast type={NotificationType.Change}
						title={	<div className="flex-1">
							<span className="font-medium">
								{successfulUndoEvents.length ?? 0} Changes
							</span> were successfully undone
						</div>}
						closeToast={closeToast}
					/>

				),
				{
					position: 'bottom-right',
				},
			);
		}

		if (successfulUpdateEvents.length > 1) {
			alephToast(
				({ closeToast }) => (
					<NotificationToast type={NotificationType.Change}
						title={<div className="flex-1">
							<span className="font-medium">
								{successfulUpdateEvents?.length ?? 0} Changes
							</span> were successfully made
						</div>}
						action={<button
							className="text-[12px] text-blue-300 hover:text-blue flex gap-[8px] items-center"
							onClick={
								() => {
									closeToast?.();
									validEvents.forEach(e => {
										e.node.setDataValue(e.column, e.oldValue, 'undoChange');
									});
									setUndoEvents([]);
								}
							}
						>
							<UndoIcon
								height={12}
								width={12}
							/> Undo
						</button>}
						closeToast={closeToast}
					/>
				),
				{
					position: 'bottom-right',
				},
			);
		}

		await Promise.all((successfulEvents ?? []).map(async (event: CellValueChangedEvent): Promise<void> => {
			try {
				event?.api?.applyServerSideTransaction({
					update: await transformRedditRows([{
						...event.node?.data ?? {},
						...event.data ?? {},
					}], event.node.data.level),
					route: (event.node.getRoute() ?? []).slice(0, -1),
				});
			} catch (e) {
				captureException(e);
			}

			event?.api?.flashCells({
				columns:  [event.column],
				rowNodes: [event.node],
			});
		}));

		setUndoEvents(curr => [...curr, ...successfulUpdateEvents]);
		setSavingEvents([]);

		setTimeout(() => {
			setUndoEvents(curr => curr.filter(e => !successfulUpdateEvents.includes(e)));
		}, 7500);
	};

	const debouncedDoUpdate = useMemo(() => debounce(doUpdates, 100), []);

	useEffect(() => {
		if (stagedChangeEvents.length > 0) {
			debouncedDoUpdate(stagedChangeEvents);
		}
	}, [JSON.stringify(stagedChangeEvents.map(e => e.newValue))]);

	const onCellValueChanged = async (e: CellValueChangedEvent) => {
		setStagedChangeEvents(curr => [
			...curr.filter(
				event => event.rowIndex !== e.rowIndex || event.colDef?.field !== e.colDef?.field,
			),
			e,
		]);
	};

	return {
		onCellValueChanged,
	};
};

export default useRedditEditPlugin;
